import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { registerLocale } from 'react-datepicker'
import en from '../locales/en.json'

import { DATE_LOCALE_MAP, LANGUAGE } from 'utils/constants/locale'
import { shouldShowTranslationKeys } from 'utils/translation-keys'
import I18NextHttpBackend from 'i18next-http-backend'

const translationKey = 'LANGUAGE_SELECTION'

const languageDetectorOptions = {
  order: ['querystring', 'cookie', 'localStorage', 'sessionStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],

  lookupQuerystring: 'lng',
  lookupCookie: translationKey,
  lookupLocalStorage: translationKey,
  lookupSessionStorage: translationKey,

  lookupFromPathIndex: 0,
  lookupFromSubdomainIndex: 0,
}

if (!localStorage.getItem(translationKey)) {
  localStorage.setItem(translationKey, LANGUAGE.en)
}

// Record `?translationKeys=1` before the router strips the query string on its first redirect;
// useLanguage reads the resulting tab flag when it applies the language.
shouldShowTranslationKeys()

// Every date picker in the app is Sunday-first, because that is what the rest of the app assumes:
// the calendar grid reads its week start off dayjs (Sunday), its week ranges are built from
// `Date#getDay()`, and date-fns' own startOfWeek default is Sunday. The date-fns locales disagree
// — enAU (our `en`), th, pl, pt, es and most others are Monday-first — so pin the week start on the
// copies handed to react-datepicker. DATE_LOCALE_MAP is deliberately left untouched: utils/datetime
// still formats with the real locale, only the picker registry is normalised.
const SUNDAY = 0

// react-datepicker resolves setDefaultLocale(lang) against its registry, so every supported
// language has to be registered up front or the pickers silently fall back to en-US
Object.entries(DATE_LOCALE_MAP).forEach(([code, locale]) => {
  if (locale) {
    registerLocale(code, { ...locale, options: { ...locale.options, weekStartsOn: SUNDAY } })
  }
})

const isTestEnv = process.env.NODE_ENV === 'test'

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(I18NextHttpBackend)
  .init({
    detection: languageDetectorOptions,
    fallbackLng: LANGUAGE.en,
    load: 'currentOnly',
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      escapeValue: false,
    },
    backend: {
      loadPath: `/locales/{{lng}}.json?v=${process.env.REACT_APP_VERSION}`, // URL to load translations with cache management
    },
    resources: {
      en: { translation: en }, // Only bundle English, fetch others dynamically
    },
    partialBundledLanguages: true,
    react: {
      useSuspense: !isTestEnv,
    },
    initImmediate: !isTestEnv,
    ...(isTestEnv && {
      resources: {
        en: { translation: require('../locales/en.json') },
      },
    }),
  })

export default i18n
